/* eslint-disable @typescript-eslint/no-non-null-assertion */
/** ***************************************************************************
 * This form is used to define methods and constants for the EVDC device form.
 * Make sure all these methods and constants are kept aligned and updated.
 * Please also ensure the UI is updated and the EvdcDeviceFormData *
 **************************************************************************** */

import { UseFormWatch } from "react-hook-form"

import {
    TAmendEvdcDevicePostBody,
    TEvdcDeviceResponseType,
    TPublishEvdcDevicePostBody,
    TSaveEVDCDeviceDraftPostBody,
} from "../../../../services/middleware/types/devices/lct/publishEvdcDeviceTypes"
import { EVDCDeviceFormData } from "../types/providerTypes"

export const EVDC_MANUFACTURER_REG = "evdcManufacturer"
export const EVDC_MODEL_REG = "evdcModel"
export const EVDC_MODEL_REF_REG = "evdcModelRef"
export const EVDC_TYPE_REG = "evdcType"
export const EVDC_MODULE_REG = "evdcModule"
export const EVDC_PHASES_REG = "evdcPhases"
export const EVDC_VOLTAGE_REG = "evdcVoltage"
export const EVDC_TOT_INPUT_RATED_CURRENT_REG = "evdcTotInputRatedCurrent"
export const EVDC_TOT_INPUT_RATED_POWER_REG = "evdcTotInputRatedPower"
export const EVDC_MAX_DEMAND_CURRENT_REG = "evdcMaxDemandCurrent"
export const EVDC_MAX_DEMAND_POWER_REG = "evdcMaxDemandPower"
export const EVDC_MODULE_INPUT_RATED_CURRENT_REG = "evdcModuleInputRatedCurrent"
export const EVDC_MODULE_INPUT_RATED_POWER_REG = "evdcModuleInputRatedPower"
export const EVDC_HARMONIC_TEST_STANDARDS_REG_REG =
    "evdcAppTestStandardsHarmonicStandards"
export const EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG =
    "evdcCommentsAppTestStandards_Notes"

export const evdcDeviceFormLabelMapping: { [key: string]: string } = {
    [EVDC_MANUFACTURER_REG]: "Manufacturer",
    [EVDC_MODEL_REG]: "Model",
    [EVDC_MODEL_REF_REG]: "Model Reference Number",
    [EVDC_TYPE_REG]: "Device Type",
    [EVDC_MODULE_REG]: "Module",
    [EVDC_PHASES_REG]: "Device Phases",
    [EVDC_VOLTAGE_REG]: "Voltage (V)",
    [EVDC_TOT_INPUT_RATED_CURRENT_REG]:
        "Total Input Rated Current (Amps per phase)",
    [EVDC_TOT_INPUT_RATED_POWER_REG]: "Total Input Rated Power (kVA)",
    [EVDC_MAX_DEMAND_CURRENT_REG]: "Max Current Demand (Amps per phase)",
    [EVDC_MAX_DEMAND_POWER_REG]: "Max Demand Power (kVA)",
    [EVDC_MODULE_INPUT_RATED_CURRENT_REG]:
        "Module Input Rated Current (Amps per phase)",
    [EVDC_MODULE_INPUT_RATED_POWER_REG]: "Module Input Rated Power (kVA)",
    [EVDC_HARMONIC_TEST_STANDARDS_REG_REG]: "Harmonic Test Standards",
    [EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG]: "Comments on Test Standards",
}

export const NON_SYS_ADMIN_EVDC_STEPS = [
    "Core EVDC Device Details",
    "Upload Supporting Files",
    "Review and Submit",
]

export const NON_SYS_ADMIN_EVDC_STEPS_AMEND = [
    "Amend Core EVDC Device Details",
    "Amend Supporting Files",
    "Review and Submit Amendment",
]

export const LCT_PHASES_OPTIONS = {
    "Single Phase": 1,
    "Three Phase": 3,
}

export const EVDC_TYPES = ["DC only", "AC Only", "DC and AC"]
export const EVDC_MODULES = ["Whole System", "DC Output", "AC Output"]
export const EVDC_VOLTAGE_OPTIONS = [230, 400]
export const EVDC_HARMONIC_TEST_STANDARDS = [
    "EN 61000-3-12",
    "EN 61000-3-2",
    "EN 61000-6-3",
    "EN 61000-6-4",
    "IEC 61000-3-4",
    "IEC 61851-21-2",
    "BS EN 61851-23",
    "N/A",
]

export const SYS_ADMIN_EVCP_STEPS = ["Manufacturer"]

/**
 * Returns EVDC Device form data where all fields are empty.
 *
 * Note: Avoid using `undefined` when providing a default value
 */
export const getEmptyEVDCDeviceFormData = (): EVDCDeviceFormData => ({
    manufacturerId: null,
    deviceMake: "",
    deviceModel: "",
    deviceModelRef: "",
    deviceType: null,
    deviceModule: null,
    devicePhases: null,
    deviceVoltage: null,
    deviceTotInputRatedCurrent: null,
    deviceTotInputRatedPower: null,
    deviceMaxDemandCurrent: null,
    deviceMaxDemandPower: null,
    deviceModuleInputRatedCurrent: null,
    deviceModuleInputRatedPower: null,
    deviceHarmonicTestStandards: null,
    deviceCommentsTestStandardsNotes: "",
    stagedAttachments: [],
    systemReference: null,
})

/** Parses form register from form data for EVDC */
export const mapEvdcRegisterNameToValues = (
    formData: EVDCDeviceFormData
): { [name: string]: NonNullable<unknown> | undefined | null } => ({
    [EVDC_MANUFACTURER_REG]: formData.manufacturerId,
    [EVDC_MODEL_REG]: formData.deviceModel,
    [EVDC_MODEL_REF_REG]: formData.deviceModelRef,
    [EVDC_TYPE_REG]: formData.deviceType,
    [EVDC_MODULE_REG]: formData.deviceModule,
    [EVDC_PHASES_REG]: formData.devicePhases,
    [EVDC_VOLTAGE_REG]: formData.deviceVoltage,
    [EVDC_TOT_INPUT_RATED_CURRENT_REG]: formData.deviceTotInputRatedCurrent,
    [EVDC_TOT_INPUT_RATED_POWER_REG]: formData.deviceTotInputRatedPower,
    [EVDC_MAX_DEMAND_CURRENT_REG]: formData.deviceMaxDemandCurrent,
    [EVDC_MAX_DEMAND_POWER_REG]: formData.deviceMaxDemandPower,
    [EVDC_MODULE_INPUT_RATED_CURRENT_REG]:
        formData.deviceModuleInputRatedCurrent,
    [EVDC_MODULE_INPUT_RATED_POWER_REG]: formData.deviceModuleInputRatedPower,
    [EVDC_HARMONIC_TEST_STANDARDS_REG_REG]:
        formData.deviceHarmonicTestStandards,
    [EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG]:
        formData.deviceCommentsTestStandardsNotes,
})

/** Gets updated evdc form data.
 *
 * @param formData - The form data
 * @param watch - The watch function from react-hook-form
 * @returns The updated form data
 */
export const getEvdcUpdatedFormData = (
    formData: EVDCDeviceFormData,
    watch: UseFormWatch<Record<string, unknown>>
): EVDCDeviceFormData => ({
    ...formData,
    deviceModel: watch(EVDC_MODEL_REG) as string,
    deviceModelRef: watch(EVDC_MODEL_REF_REG) as string,
    deviceType: watch(EVDC_TYPE_REG) as string,
    deviceModule: watch(EVDC_MODULE_REG) as string,
    devicePhases: watch(EVDC_PHASES_REG) as number,
    deviceVoltage: watch(EVDC_VOLTAGE_REG) as number,
    deviceTotInputRatedCurrent: watch(
        EVDC_TOT_INPUT_RATED_CURRENT_REG
    ) as number,
    deviceTotInputRatedPower: watch(EVDC_TOT_INPUT_RATED_POWER_REG) as number,
    deviceMaxDemandCurrent: watch(EVDC_MAX_DEMAND_CURRENT_REG) as number,
    deviceMaxDemandPower: watch(EVDC_MAX_DEMAND_POWER_REG) as number,
    deviceModuleInputRatedCurrent: watch(
        EVDC_MODULE_INPUT_RATED_CURRENT_REG
    ) as number,
    deviceModuleInputRatedPower: watch(
        EVDC_MODULE_INPUT_RATED_POWER_REG
    ) as number,
    deviceHarmonicTestStandards: watch(
        EVDC_HARMONIC_TEST_STANDARDS_REG_REG
    ) as string,
    deviceCommentsTestStandardsNotes: watch(
        EVDC_COMMENTS_APP_TEST_STANDARDS_NOTES_REG
    ) as string,
})

/**
 * Generates create EVDC post body from form data
 * @param {EVDCDeviceFormData} formData - data input from the form
 * @param {string} attachmentUUID - device attachment UUID
 * @returns {TPublishEvdcDevicePostBody} - the post body for the publish LCT device request
 */
export const createEvdcPostBodyFromData = (
    formData: EVDCDeviceFormData,
    attachmentUUID: string,
    systemReference?: string
): TPublishEvdcDevicePostBody => ({
    device_type: "EVDC",
    device_data: {
        ManufacturerID: formData.manufacturerId ?? undefined,
        Model: formData.deviceModel,
        Make: formData.deviceMake,
        ModelReference: formData.deviceModelRef,
        EVCPType: formData.deviceType as string,
        Module: formData.deviceModule as string,
        Phases: formData.devicePhases! as number,
        Voltage: formData.deviceVoltage! as number,
        TotalEVCPInputRatedCurrent:
            formData.deviceTotInputRatedCurrent! as number,
        TotalEVCPMaxDemandCurrent: formData.deviceMaxDemandCurrent! as number,
        TotalEVCPInputRatedPower: formData.deviceTotInputRatedPower! as number,
        TotalEVCPMaxDemandPower: formData.deviceMaxDemandPower! as number,
        ModuleInputRatedCurrent:
            formData.deviceModuleInputRatedCurrent! as number,
        ModuleInputRatedPower: formData.deviceModuleInputRatedPower! as number,
        AppTestStandards_HarmonicStandards:
            formData.deviceHarmonicTestStandards as string,
        Comments_AppTestStandards_Notes:
            formData.deviceCommentsTestStandardsNotes,
        Attachment_UUID: attachmentUUID,
        SystemReference: systemReference,
    },
})

/**
 * Generates amend EVDC post body from form data
 * @param {EVDCDeviceFormData} formData - data input from the form
 * @param {string} attachmentUUID - device attachment UUID
 * @returns {TPublishEvdcDevicePostBody} - the post body for the publish LCT device request
 */
// TODO - add in assessment fields
export const amendEvdcPostBodyFromData = (
    formData: EVDCDeviceFormData,
    attachmentUUID: string,
    systemReference: string
): TAmendEvdcDevicePostBody => ({
    device_type: "EVDC",
    device_data: {
        ManufacturerID: formData.manufacturerId ?? undefined,
        Model: formData.deviceModel,
        Make: formData.deviceMake,
        ModelReference: formData.deviceModelRef,
        EVCPType: formData.deviceType as string,
        Module: formData.deviceModule as string,
        Phases: formData.devicePhases! as number,
        Voltage: formData.deviceVoltage! as number,
        TotalEVCPInputRatedCurrent:
            formData.deviceTotInputRatedCurrent! as number,
        TotalEVCPMaxDemandCurrent: formData.deviceMaxDemandCurrent! as number,
        TotalEVCPInputRatedPower: formData.deviceTotInputRatedPower! as number,
        TotalEVCPMaxDemandPower: formData.deviceMaxDemandPower! as number,
        ModuleInputRatedCurrent:
            formData.deviceModuleInputRatedCurrent! as number,
        ModuleInputRatedPower: formData.deviceModuleInputRatedPower! as number,
        AppTestStandards_HarmonicStandards:
            formData.deviceHarmonicTestStandards as string,
        Comments_AppTestStandards_Notes:
            formData.deviceCommentsTestStandardsNotes,
        SystemReference: systemReference,
        Attachment_UUID: attachmentUUID,
    },
})

/**
 *  Maps the device responses from API endpoints to Device form data
 * @param {TEVDCDeviceResponseType} output - the response from the API
 * @returns {EVDCDeviceFormData} - the form data for the EVDC device
 */
export const mapEvdcDeviceResponseToDeviceFormData = <
    T extends TEvdcDeviceResponseType,
>(
    output?: T
): EVDCDeviceFormData => {
    if (!output) {
        return getEmptyEVDCDeviceFormData()
    }

    return {
        manufacturerId: output.ManufacturerID,
        deviceMake: output.Make ?? "",
        deviceModel: output.Model ?? "",
        deviceModelRef: output.ModelReference ?? "",
        deviceType: output.EVCPType,
        deviceModule: output.Module,
        devicePhases: output.Phases,
        deviceVoltage: output.Voltage,
        deviceTotInputRatedCurrent: output.TotalEVCPInputRatedCurrent,
        deviceTotInputRatedPower: output.TotalEVCPInputRatedPower,
        deviceMaxDemandCurrent: output.TotalEVCPMaxDemandCurrent,
        deviceMaxDemandPower: output.TotalEVCPMaxDemandPower,
        deviceModuleInputRatedCurrent: output.ModuleInputRatedCurrent,
        deviceModuleInputRatedPower: output.ModuleInputRatedPower,
        deviceHarmonicTestStandards: output.AppTestStandards_HarmonicStandards,
        deviceCommentsTestStandardsNotes:
            output.Comments_AppTestStandards_Notes ?? "",
        stagedAttachments: [],
        systemReference: output.SystemReference ?? null,
    }
}

export const createEVDCDeviceDraftPostBody = (
    formData: EVDCDeviceFormData,
    attachmentUUID: string,
    isAmendment: boolean,
    systemReference?: string
): TSaveEVDCDeviceDraftPostBody => ({
    device_type: "EVDC",
    device_data: {
        ManufacturerID: formData.manufacturerId ?? undefined,
        Model: formData.deviceModel,
        Make: formData.deviceMake,
        ModelReference: formData.deviceModelRef,
        EVCPType: formData.deviceType as string,
        Module: formData.deviceModule as string,
        Phases: formData.devicePhases! as number,
        Voltage: formData.deviceVoltage! as number,
        TotalEVCPInputRatedCurrent:
            formData.deviceTotInputRatedCurrent! as number,
        TotalEVCPMaxDemandCurrent: formData.deviceMaxDemandCurrent! as number,
        TotalEVCPInputRatedPower: formData.deviceTotInputRatedPower! as number,
        TotalEVCPMaxDemandPower: formData.deviceMaxDemandPower! as number,
        ModuleInputRatedCurrent:
            formData.deviceModuleInputRatedCurrent! as number,
        ModuleInputRatedPower: formData.deviceModuleInputRatedPower! as number,
        AppTestStandards_HarmonicStandards:
            formData.deviceHarmonicTestStandards as string,
        Comments_AppTestStandards_Notes:
            formData.deviceCommentsTestStandardsNotes,
        SystemReference: systemReference,
        Attachment_UUID: attachmentUUID,
    },
    is_amendment: isAmendment,
})
